/* eslint-disable */
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DashIcon from "components/icons/DashIcon";
import { useTranslation } from "react-i18next";

// Category definitions with display order and labels
const CATEGORIES: { key: string; label: string | null }[] = [
  { key: "main", label: null },
  { key: "operations", label: "Operações" },
  { key: "admin", label: "Gestão" },
  { key: "settings", label: null },
];

// Settings sub-menu items
const SETTINGS_CHILDREN = [
  { label: "Geral", path: "settings/general" },
  { label: "Categorias", path: "settings/categories" },
  { label: "Tipos de Veículos", path: "settings/vehicle-types" },
  { label: "Área de Cobertura", path: "settings/coverage-area" },
  { label: "Payment Methods", path: "settings/payment-methods" },
  { label: "Mercado Pago", path: "settings/mercadopago" },
  { label: "Help Center", path: "settings/help-center" },
  { label: "Tutoriais", path: "settings/tutorials" },
];

export const SidebarLinks = (props: { routes: RoutesType[] }): JSX.Element => {
  const { t } = useTranslation();
  let location = useLocation();
  const [settingsActive, setSettingsActive] = useState(
    location.pathname.includes('/settings')
  );
  const navigate = useNavigate();
  const { routes } = props;

  const activeRoute = (routeName: string) => {
    return location.pathname.includes(routeName);
  };

  const renderLink = (route: RoutesType, index: number) => {
    if (route.secondary) return null;
    const isActive = activeRoute(route.path);
    const isSettings = route.name === "Settings";

    return (
      <React.Fragment key={index}>
        <div
          onClick={() => {
            if (isSettings) {
              const willOpen = !settingsActive;
              setSettingsActive(willOpen);
              // Only navigate to main settings page when opening the submenu
              // and we're not already on a settings subpage
              if (willOpen && !location.pathname.includes('/settings/')) {
                navigate(route.layout + "/" + route.path);
              }
            } else {
              setSettingsActive(false);
              navigate(route.layout + "/" + route.path);
            }
          }}
        >
          <div
            className={`relative mb-2 flex hover:cursor-pointer rounded-xl mx-2 py-3 px-4 transition-all duration-200
              ${isActive
                ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/10 border border-cyan-500/30'
                : 'hover:bg-white/5 border border-transparent'
              }`}
          >
            <li
              className="flex cursor-pointer items-center w-full"
              key={index}
            >
              <span
                className={`text-lg ${isActive
                  ? "text-cyan-400"
                  : "text-gray-400 group-hover:text-gray-300"
                  }`}
              >
                {route.icon ? route.icon : <DashIcon />}
              </span>
              <p
                className={`leading-1 ml-4 flex text-sm ${isActive
                  ? "font-semibold text-white"
                  : "font-medium text-gray-400 hover:text-gray-200"
                  }`}
              >
                {t(`${route.name}`)}
              </p>
              {/* Chevron for Settings */}
              {isSettings && (
                <svg
                  className={`ml-auto h-4 w-4 text-gray-400 transition-transform duration-200 ${settingsActive ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </li>
            {isActive && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-1 rounded-l-full bg-gradient-to-b from-cyan-400 to-purple-500" />
            )}
          </div>
        </div>
        {/* Settings expanded sub-menu */}
        {settingsActive && isSettings && (
          <div className="ml-8 mr-4 mt-1 mb-2 flex flex-col space-y-1 border-l-2 border-cyan-500/20 pl-4">
            {SETTINGS_CHILDREN.map((child, i) => {
              const fullPath = route.layout + "/" + child.path;
              const isChildActive = child.path === "settings"
                ? location.pathname === fullPath || location.pathname === fullPath + "/"
                : location.pathname.includes(child.path);
              return (
                <Link
                  key={i}
                  className={`text-sm font-medium transition-colors py-1.5 ${isChildActive
                    ? "text-cyan-400"
                    : "text-gray-400 hover:text-cyan-400"
                    }`}
                  to={route.layout + "/" + child.path}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span>{t(child.label)}</span>
                </Link>
              );
            })}
          </div>
        )}
      </React.Fragment>
    );
  };

  const createGroupedLinks = () => {
    const elements: JSX.Element[] = [];

    CATEGORIES.forEach((category, catIndex) => {
      // Filter routes for this category
      const categoryRoutes = routes.filter(
        (r) => r.category === category.key && !r.secondary
      );
      if (categoryRoutes.length === 0) return;

      // Visual separator before sections (not for the first one)
      if (catIndex > 0) {
        elements.push(
          <div key={`sep-${category.key}`} className="mx-6 my-3 border-t border-white/5" />
        );
      }

      // Section header
      if (category.label) {
        elements.push(
          <p
            key={`header-${category.key}`}
            className="mx-6 mb-2 mt-1 text-[10px] font-bold uppercase tracking-widest text-gray-500"
          >
            {category.label}
          </p>
        );
      }

      // Render routes in this category
      categoryRoutes.forEach((route) => {
        const globalIndex = routes.indexOf(route);
        const el = renderLink(route, globalIndex);
        if (el) elements.push(el);
      });
    });

    // Also render any non-secondary routes without a category (fallback)
    const uncategorized = routes.filter(
      (r) => !r.category && !r.secondary
    );
    uncategorized.forEach((route) => {
      const globalIndex = routes.indexOf(route);
      const el = renderLink(route, globalIndex);
      if (el) elements.push(el);
    });

    return elements;
  };

  return <>{createGroupedLinks()}</>;
};

export default SidebarLinks;
